import hermiteResize from "hermite-resize";

const canvasUtils = {
  createCanvasElement({ width, height }) {
    const canvas = document.createElement("canvas");
    canvas.width = Number(width);
    canvas.height = Number(height);
    return canvas;
  },

  /**
     * Wrapper function to use the Promise API to get Base64 Data Strings
     * From Blobs
     * @param {Object} Blob
     * @returns {String} Data:string
     */
  async convertBlobToBase64(blob) {
    return await this.blobToBase64(blob);
  },
  /**
   *
   * @param {Object} blob
   * @returns File Data URL
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    })
  },

  getImageFromUrl(url) {
    return new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        return res(img);
      };
      img.setAttribute("crossOrigin", "anonymous");
      img.onerror = err => rej(err);
      img.src = url;
    });
  },

  /**
   * @param {String} url (File | Blob)
   * @returns Blob Object
   */
  async getBlobFromUrl(url) {
    return await fetch(url).then(r => r.blob());
  },
  /**
   * @param {Element} canvas
   * @returns {Object} Full Blob Object
   */
  getBlobFromCanvas(canvas) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  },

  /**
   * Wrapper function to get resolved toBlob URLs from Canvas
   * @param {Element} canvas
   * @returns {String}
   */
  async setImageUrlFromCanvas(canvas) {
    return await this.getImageUrlFromCanvas(canvas);
  },
  /**
   * @param {Element} canvas
   * @returns URL blob:string
   */
  getImageUrlFromCanvas(canvas) {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(URL.createObjectURL(blob));
      }, 'image/png');
    });
  },
  convertHexToRGBA({ hex, alpha = 0.5 }) {
    const color = hex.replace("#", "");
    const bigint = parseInt(color, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${Number(alpha)})`;
  },
  applyImageOverlay({ imageBuffer, imageOverlay, width, height }) {
    let canvas = this.createCanvasElement({ width, height });
    const ctx = canvas.getContext("2d");
    ctx.drawImage(imageBuffer, 0, 0);
    if (imageOverlay) {
      ctx.drawImage(imageOverlay, 0, 0);
    }
    return this.getImageUrlFromCanvas(canvas)
      .then(url => this.getImageFromUrl(url))
      .then(image => {
        canvas = null;
        return Promise.resolve(image);
      });
  },
  applyColorOverlay({ imageBuffer, color, alpha, width, height }) {
    let canvas = this.createCanvasElement({ width, height });
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = this.convertHexToRGBA({ hex: color, alpha });
    ctx.drawImage(imageBuffer, 0, 0);
    ctx.fillRect(0, 0, parseInt(width), parseInt(height));
    return this.getImageUrlFromCanvas(canvas)
      .then(url => this.getImageFromUrl(url))
      .then(image => {
        canvas = null;
        return Promise.resolve(image);
      });
  },
  resizeImage({ image, width, height }) {
    const imageWidth = image.width;
    const imageHeight = image.height;
    let canvas = this.createCanvasElement({
      width: imageWidth,
      height: imageHeight
    });
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0); //draw image
    return new Promise((resolve, reject) => {
      const HERMITE = new hermiteResize();
      HERMITE.resample(canvas, width, height, true, () => {
        this.getImageUrlFromCanvas(canvas)
          .then(url => this.getImageFromUrl(url))
          .then(resizedImage => {
            canvas = null;
            image = null;
            resolve(resizedImage);
          })
          .catch(reject);
      });
    });
  }
};

export default canvasUtils;
